import { Injectable, OnDestroy } from '@angular/core';
import { isNil } from 'lodash';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { AzureBlobStorageService } from '../api/azure-blob-storage.service';
import { LampCatalogueService } from '../api/lamp-catalogue.service';
import { environment } from '@env/environment';
import { StreetlightApiService } from '../api/streetlight.service';
import { CountedLampCatalogue } from '@app/shared/models/lamp-catalogue/lamp-catalogue';
import { CountedLampDetails, LampDetails } from '@app/shared/models/lamp-catalogue/lamp-details';
import { LuminaireCategory } from '@app/shared/models/luminaire-category/luminaire-category';

@Injectable({
  providedIn: 'root',
})
export class LampCatalogueStoreService implements OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  private readonly lampCatalogueStore: BehaviorSubject<CountedLampDetails> = new BehaviorSubject<CountedLampDetails>({} as CountedLampDetails);
  private readonly manufacturerStore: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  public readonly lampCatalogue$: Observable<CountedLampDetails> = this.lampCatalogueStore.asObservable();
  public readonly manufacturers$: Observable<string[]> = this.manufacturerStore.asObservable();

  constructor(
    private azureBlobStorageService: AzureBlobStorageService,
    private lampCatalogueService: LampCatalogueService,
    private streetLightsService: StreetlightApiService
  ) {
    this.getLamps().pipe(takeUntil(this.unsubscribe$)).subscribe();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public getLamps(searchParam?: string): Observable<CountedLampDetails> {
    const manufacturerList: string[] = [];

    return this.lampCatalogueService.getLampCatalogue(searchParam).pipe(
      map((catalogue: CountedLampCatalogue) => {
        const countedLampDetails: CountedLampDetails = { ...(catalogue as CountedLampDetails) };
        const updatedData = countedLampDetails.data.map((lampDetails: LampDetails) => {
          const imageObservable =
            isNil(lampDetails.imageName) || lampDetails.imageName === 'undefined'
              ? this.azureBlobStorageService.getBlobImageSafeUrl(`${environment.tenant}/assets/images/lamps/DefaultLamp.png`)
              : this.azureBlobStorageService.getBlobImageSafeUrl(`${environment.tenant}/assets/images/lamps/${lampDetails.imageName}`);
          manufacturerList.push(lampDetails.manufacturer);
          return { ...lampDetails, imageObservable };
        });

        countedLampDetails.data = updatedData;

        return countedLampDetails;
      }),
      tap((catalogue: CountedLampDetails) => {
        const uniqManufacturerList = Array.from(new Set(manufacturerList));
        this.manufacturerStore.next(uniqManufacturerList);
        this.lampCatalogueStore.next(catalogue);
      })
    );
  }

  public getLuminaireCategories(): Observable<LuminaireCategory[]> {
    return this.streetLightsService.getLuminaireCategories();
  }

  public createLampEntry(lampDetails: FormData): Observable<Object> {
    return this.lampCatalogueService.createLampCatalogue(lampDetails);
  }

  public updateLampCatalogue(lampGuid: string, lampDetails: FormData): Observable<Object> {
    return this.lampCatalogueService.updateLampCatalogue(lampDetails, lampGuid);
  }

  public deleteLampEntry(lampGuid: string): Observable<Object> {
    return this.lampCatalogueService.deleteLampCatalogue(lampGuid);
  }
}
